// Centralized helpers & types for the analytics system

import { matchesPattern } from '../../utils';
import { PersonData, Payment, Claim, Leave } from '../../data-models';
import { log } from '../../utils/log';
import {
  Category,
  toHTMLElement,
  getDataGtmDlFromElement,
  normalizeDlFields,
  categoryFromClass,
  pack,
  lower,
  categoryFromInputType,
} from './kendo-helpers';
import { isTrackingDeclined } from '..';

const pathnamePageTitleMap: { [name: string]: string } = {
  '/app': 'summary',
  '/app/absence-history': 'absences',
  '/app/absence-history/*': 'absence history calendar',
  '/app/leave-plan/*': 'history calendar',
  '/app/leave-plan': 'absence detail',
  '/app/tasks': 'tasks',
  '/app/task/*/details/*': 'task detail',
  '/app/payments': 'payments',
  '/app/documents': 'documents',
  '/app/time': 'available time',
  '/app/profile': 'my preferences',
  '/app/task/initiate/extension_request': 'file extension request form',
  '/app/task/initiate/last_day_worked': 'last day worked task',
  '/app/task/initiate/delivery_details': 'delivery details task',
  '/app/task/initiate/sx_details': 'surgery details task',
  '/app/task/initiate/rtw_confirmation': 'return to work task',
  '/app/form/intermittent-absence': 'report intermittent absence form',
  '/app/form/e-signature': 'sign e-signature authorization form',
  '/app/form/physician-information': 'update physician information form',
  '/app/form/scheduled-callback': 'schedule a callback form',
  '/app/upload': 'upload a document',
  '/app/page/feedback': 'leave feedback',
  '/app/supplemental': 'file a claim – select supplemental coverage',
  '/app/start': 'file a claim - what are you filing for?',
  '/app/learning-hub': 'learning hub',
  '/app/duplicate': 'simmilar claim found',
};

const getPageTitle = () => {
  const path = globalThis.location.pathname;
  const docTitle = document?.title;

  const titleKey = Object.keys(pathnamePageTitleMap).find((key) =>
    matchesPattern(path, key),
  );

  let title = titleKey ? pathnamePageTitleMap[titleKey] : docTitle;

  // task detail needs the detail appended
  if (title === 'task detail') {
    const pathSegments = path.split('/');
    const taskId = pathSegments[pathSegments?.length - 1];

    title = `${title} - ${taskId}`;
  }

  if (path.startsWith('/app/form/') && !titleKey) {
    const formId = path.split('/').pop(); // last segment
    title = `file a claim - ${formId}`;
  }

  log.debug(`Analytics title: ${title}`);
  return title;
};

// Enrich a single payment with benefit-derived fields (matched by benefitId),
// mirroring how the payments UI joins benefits onto payments.
const enrichPayment = (payment: Payment, benefits?: Claim['benefits']) => {
  const benefit = benefits?.find((b) => b.benefitId === payment.benefitId);
  return {
    ...payment,
    approvedThruDate: benefit?.approvedThruDate ?? null,
    checkCutting: benefit?.checkCutting ?? null,
  };
};

// Flatten and enrich all payments across a list of claims/leaves.
const mapCasePayments = (list?: Array<Claim | Leave>) =>
  (list ?? []).flatMap((c) =>
    (c?.payments ?? []).map((payment) => enrichPayment(payment, c?.benefits)),
  );

// Collect the upcoming (next) payment for each case that has one.
const mapCaseNextPayments = (list?: Array<Claim | Leave>) =>
  (list ?? []).map((c) => c?.nextPayment).filter((np) => np?.nextPaymentDate);

export const createGA4Data = (
  personData: PersonData | undefined,
  hasCommonLandingPage = false,
) => {
  const person = personData?.person ?? {};
  const cases = personData?.cases ?? {};
  const tasksArray = personData?.tasks?.tasks ?? [];
  const lettersArray = personData?.documents?.letters ?? [];
  const address = person.addresses?.[0] ?? {};

  const claims = (cases?.claims ?? []).map((c) => ({
    claimId: c?.claimId ?? 'unknown',
    status: c?.status ?? 'unknown',
    claimType: c?.claimType ?? 'unknown',
    createdDate: c?.createdDate ?? null,
    expectedReturnToWorkDate: c?.expectedReturnToWorkDate ?? 'unknown',
    disabilityClaimIncurredDate: c?.disability?.claimIncurredDate ?? 'unknown',
    disabilityEmployeeDateLastWorked:
      c?.disability?.employeeDateLastWorked ?? 'unknown',
    disabilityExpectedReturnToWorkDate:
      c?.disability?.expectedReturnToWorkDate ?? 'unknown',
    disabilityFirstDayMissedWork:
      c?.disability?.firstDayMissedWork ?? 'unknown',
    pregnancyActualDeliveryDt: c?.pregnancy?.actualDeliveryDt ?? 'unknown',
    pregnancyComplications: c?.pregnancy?.complications ?? 'unknown',
    pregnancyDeliveryType: c?.pregnancy?.deliveryType ?? 'unknown',
    pregnancyExpectedDeliveryDate:
      c?.pregnancy?.expectedDeliveryDate ?? 'unknown',
    parentId: c?.parentId ?? 'unknown',
  }));

  const leaves = (cases?.leaves ?? []).map((l) => ({
    absenceType: l?.absenceType ?? 'unknown',
    leaveId: l?.leaveId ?? 'unknown',
    requestStatus: l?.requestStatus ?? 'unknown',
    reason: l?.reason ?? 'unknown',
    reasonQualifier1: l?.reasonQualifier1 ?? 'unknown',
    reasonQualifier2: l?.reasonQualifier2 ?? 'unknown',
    startDate: l?.startDate ?? 'unknown',
    endDate: l?.endDate ?? 'unknown',
    parentId: l?.parentId ?? 'unknown',
  }));

  const tasks = (tasksArray ?? []).map((t) => ({
    taskId: t?.taskId ?? 'unknown',
    taskCategory: t?.taskCategory ?? 'unknown',
    taskCodeName: t?.taskCodeName ?? 'unknown',
    taskDueDate: t?.taskDueDate ?? 'unknown',
    submitDate: t?.submitDate ?? 'unknown',
    taskStatus: t?.taskStatus ?? 'unknown',
    createdDate: t?.createdDate ?? 'unknown',
    notificationCaseId: t?.notificationCaseId ?? 'unknown',
    leaveReason: t?.leaveReason ?? 'unknown',
    assignee: t?.assignee ?? 'unknown',
  }));
  const payments = {
    claims: mapCasePayments(cases?.claims),
    leaves: mapCasePayments(cases?.leaves),
    nextPayments: {
      claims: mapCaseNextPayments(cases?.claims),
      leaves: mapCaseNextPayments(cases?.leaves),
    },
  };

  return {
    event: 'custom_pageview',
    digital_asset: 'total leave - consumer',
    env: getEnv(),
    journey_name: 'solutions',
    page_title: getPageTitle(),

    user_id: person?.eid ?? null,
    user_role: 'employee',
    first_name: person?.firstName ?? null,
    last_name: person?.lastName ?? null,
    dob: person?.dob ?? null,
    gender: person?.gender ?? null,
    state: person?.workState ?? null,
    city: address?.city ?? null,
    eligibility_id: person?.eligibilityId ?? null,
    employer_id: person?.employerEid ?? null,
    employer_name: person?.employerName ?? null,
    company_code: person?.companyCode ?? null,
    customer_number: person?.customerNumber ?? null,
    first_time_login: person?.firstTimeLogin?.split('T')[0] ?? null,
    comm_preferences_text_message:
      person?.preferences?.communicationPreferences?.find(
        (pref) => pref.preferenceType === 'Texting',
      )?.preferenceValue ?? null,
    comm_preferences_email:
      person?.preferences?.communicationPreferences?.find(
        (pref) => pref.preferenceType === 'Email',
      )?.preferenceValue ?? null,
    comm_preferences_digital_letters:
      person?.preferences?.communicationPreferences?.find(
        (pref) => pref.preferenceType === 'Digital',
      )?.preferenceValue ?? null,
    comm_preferences_eConsent:
      person?.preferences?.communicationPreferences?.find(
        (pref) => pref.preferenceType === 'eConsent',
      )?.preferenceValue ?? null,
    vb_coverage: person?.vbCoverages
      ? JSON.stringify(person.vbCoverages)
      : null,
    email: person?.loginId ?? null,
    has_common_landing_page: hasCommonLandingPage,

    // Collections
    claims: JSON.stringify(claims ?? []),
    leaves: JSON.stringify(leaves ?? []),
    tasks: JSON.stringify(tasks ?? []),
    payments: JSON.stringify(payments),

    lettersCount: (lettersArray ?? []).length,
    tasksCount: (tasksArray ?? []).length,
  };
};

const getEnv = () => {
  switch (globalThis.location.origin) {
    case 'https://portal.unum.com':
      return 'prod';
    case 'https://portal-ua.unum.com':
      return 'acpt';
    case 'https://portal-itest.unum.com':
      return 'itest';
    default:
      return 'dev';
  }
};

export type GAParams = {
  action: string;
  label?: string;
  category?: string;
  attribute1?: string;
  attribute2?: string;
  attribute3?: string;
  attribute4?: string;
  [k: string]: string | number | boolean | undefined;
};

// Config: Options for tracking behavior (allowed params, max lengths, etc.)
export type Config = {
  allowedParams: string[] | null;
  maxKeyLength: number;
  maxValueLength: number;
  trackTextOn: 'blur' | 'change';
  captureTextValue: boolean;
  valueMask: (v: string) => string;
  gtagFirst: boolean;
  mirrorToDataLayer: boolean;
  dataLayerEventName: string;
};

// DEFAULTS: Default configuration values for analytics tracking
export const DEFAULTS: Config = {
  allowedParams: null, // allow ...rest by default
  maxKeyLength: 40,
  maxValueLength: 500,
  trackTextOn: 'blur',
  captureTextValue: true,
  // default: don't send values
  valueMask: (v) => v.slice(0, 0),
  gtagFirst: true, // send to GA4 via gtag()
  mirrorToDataLayer: true, // also push custom event into dataLayer? (on)
  dataLayerEventName: 'custom_event',
};

// Module-scope state so we can cleanly detach
export const state: {
  instanceKey?: symbol;
  clickHandler?: (e: Event) => void;
  changeHandler?: (e: Event) => void;
  blurHandler?: (e: Event) => void;
  gtagCheckId?: number;
} = {};

export type Q = { name: string; params: Record<string, any> };
export const queue: Q[] = [];

// sendGA4Event: Send or queue events to GA4 via gtag
export function sendGA4Event(name: string, params?: Record<string, any>) {
  if (typeof (globalThis as any).window?.gtag === 'function') {
    (globalThis as any).window.gtag('event', name, params ?? {});
  } else {
    queue.push({ name, params: params ?? {} });
  }
}

// textFromNode: Extract readable text from an element for labeling
export function textFromNode(el: Element | null): string {
  if (!el) return '';
  const raw = (
    el.getAttribute('aria-label') ??
    (el as HTMLElement).getAttribute?.('data-label') ??
    (el as HTMLElement).innerText ??
    el.textContent ??
    ''
  ).trim();
  return raw.replaceAll(/\s+/g, ' ');
}

// fieldName: Get descriptive name for form fields
export function fieldName(el: Element | null): string {
  if (!el) return '';

  const ds = (el as HTMLElement).dataset || {};
  return (
    ds.field ??
    el.getAttribute('name') ??
    el.getAttribute('id') ??
    el.getAttribute('aria-label') ??
    el.getAttribute('placeholder') ??
    ''
  );
}

// safeParseJSON: Safely parse JSON strings from attributes
export function safeParseJSON<T = unknown>(s: string | null): T | null {
  if (!s) return null;
  try {
    return JSON.parse(s);
  } catch {
    return null;
  }
}

// sanitizeKey: Validate keys against allowed format
export function sanitizeKey(k: string | undefined | null): string | null {
  if (typeof k !== 'string') return null;
  return /^[A-Za-z]\w{0,39}$/.test(k) ? k : null;
}

export function isUUID(value: string): boolean {
  const uuidRegex =
    /^[0-9a-f]{8}-[0-9a-f]{4}-[1-5][0-9a-f]{3}-[89ab][0-9a-f]{3}-[0-9a-f]{12}$/i;
  return uuidRegex.test(value);
}

// create helpers that depend on a specific CONFIG instance
export function makeAnalyticsHelpers(CONFIG: Config) {
  // sanitizeValue: Ensure values are safe and within length limits
  function sanitizeValue(v: unknown): unknown {
    if (v === null || v === undefined) return null;
    const t = typeof v;
    if (t === 'object') return null; // flat only
    if (t === 'string' && (v as string).length > CONFIG.maxValueLength)
      return (v as string).slice(0, CONFIG.maxValueLength);
    return v;
  }

  function isAllowedKey(k: string): boolean {
    return !CONFIG.allowedParams || CONFIG.allowedParams.includes(k);
  }

  // mergeParams: Combine and sanitize parameter objects
  function mergeParams(
    base?: Record<string, any>,
    extra?: Record<string, any>,
  ) {
    const out: Record<string, any> = {};
    function addAll(src?: Record<string, any>) {
      if (!src || typeof src !== 'object') return;
      for (const k of Object.keys(src)) {
        const ck = sanitizeKey(k);
        if (!ck || !isAllowedKey(ck)) continue;
        const v = sanitizeValue((src as any)[k]);
        if (v !== null) out[ck] = v;
      }
    }
    addAll(base);
    addAll(extra);
    return out;
  }

  function attrsToParams(el: Element | null): Record<string, any> {
    const params: Record<string, any> = {};
    if (!el) return params;
    const raw = (el as HTMLElement).dataset.gtmDl;
    if (!raw) return params; // no data-gtm-dl
    const parsed = safeParseJSON(raw);

    if (parsed && typeof parsed === 'object') {
      return mergeParams(params, parsed as Record<string, any>);
    }
    return params;
  }

  function hasGtmDlAndAttribute1(el: Element | null): boolean {
    if (!el) return false;
    const raw = (el as HTMLElement).dataset.gtmDl;
    if (!raw) return false; // no data-gtm-dl
    const parsed = safeParseJSON(raw);
    if (!parsed || typeof parsed !== 'object') return false;
    const a3 = (parsed as any).attribute1;
    if (a3 === null || a3 === undefined) return false;
    const str = String(a3).trim();
    return str.length > 0;
  }

  // deliver: Dispatch event to GA4 and/or dataLayer
  function deliver(eventName: string, params: Record<string, any>) {
    const name = eventName || 'custom_event';
    const safe = mergeParams({}, params);
    const RESERVED = new Set(['event', 'event_name', 'ts']);
    const flattened = Object.fromEntries(
      Object.entries(safe).filter(([k]) => !RESERVED.has(k)),
    );
    if (CONFIG.gtagFirst) {
      sendGA4Event(name, safe);
      if (CONFIG.mirrorToDataLayer) {
        (globalThis as any).window.dataLayer =
          (globalThis as any).window.dataLayer || [];
        (globalThis as any).window.dataLayer.push({
          event: CONFIG.dataLayerEventName,
          event_name: name,
          ...flattened,
          ts: Date.now(),
        });
      }
    } else {
      (globalThis as any).window.dataLayer =
        (globalThis as any).window.dataLayer || [];
      (globalThis as any).window.dataLayer.push({
        event: CONFIG.dataLayerEventName,
        event_name: name,
        ...flattened,
        ts: Date.now(),
      });
    }
  }

  // handleTextInput: Capture text input details (length, optional value masking)
  function handleTextInput(el: HTMLInputElement | HTMLTextAreaElement) {
    const base: Record<string, any> = {
      category: 'text_input',
      action: 'text_input',
      label: fieldName(el),
    };
    const v = String((el as any).value ?? '');
    const params = mergeParams(base, attrsToParams(el));
    (params as any).filled = v.length > 0;
    (params as any).value_length = v.length;
    const capture = (el as any).dataset?.gtmCapture as string | undefined;
    if (capture === 'value' && CONFIG.captureTextValue) {
      (params as any).value = CONFIG.valueMask(v);
    }
    deliver('text_input', params);
  }

  return {
    sanitizeValue,
    isAllowedKey,
    mergeParams,
    attrsToParams,
    hasGtmDlAndAttribute1,
    deliver,
    handleTextInput,
  };
}

interface AnalyticsPayload {
  event: string;
  category?: string;
  action?: string;
  label?: string;
  [key: string]: any;
}

/**
 * Infer an input category and action (e.g., 'text', 'select', 'checkbox', 'radio', 'button', 'date', 'number', etc.)
 * from either a plain HTML control or a Kendo widget container.
 */

export function getInputDetailsFromElement(el: any): {
  category: string;
  action: string;
} {
  const dom = toHTMLElement(el);
  if (!dom) return pack('unknown');

  const tag = lower(dom.tagName);
  const typeAttr = lower(dom.getAttribute?.('type') ?? '');
  const roleAttr = lower(dom.getAttribute?.('role') ?? '');
  const cls = lower(String(dom.className || ''));

  // 1) Native tag-based categorization
  if (tag === 'input') return pack(categoryFromInputType(typeAttr));
  if (tag === 'select') return pack('dropdown');
  if (tag === 'textarea') return pack('text');
  if (tag === 'button') return pack('button');

  // 2) ARIA role hints
  const roleCategory = ROLE_TO_CATEGORY[roleAttr];
  if (roleCategory) return pack(roleCategory);

  // 3) Kendo-specific class hints
  const classCategory = categoryFromClass(cls);
  if (classCategory) return pack(classCategory);

  // 4) Try nearest native child (handles widgets with nested inputs)
  const inputChild =
    dom.querySelector?.('input, select, textarea, button') ||
    dom.shadowRoot?.querySelector?.('input, select, textarea, button') ||
    null;

  return inputChild ? getInputDetailsFromElement(inputChild) : pack('unknown');
}

/* -------------------------- Data tables -------------------------- */

const ROLE_TO_CATEGORY: Record<string, Category> = {
  combobox: 'dropdown',
  listbox: 'dropdown',
  spinbutton: 'text',
  switch: 'checkbox',
  textbox: 'text',
  radio: 'text', // fallback rule
  checkbox: 'checkbox',
  button: 'button',
  slider: 'text', // fallback
};

type DlProps = Record<string, any> | string | null | undefined;

// Main analytics push for Kendo components - seperate from all other components
// due to Kendo issues
export function pushAnalyticsDataLayer(
  hostEl?: HTMLElement | null,
  propsDataGtmDl?: DlProps,
  componentId?: string,
) {
  if (isTrackingDeclined()) {
    return;
  }

  const fromDomAttr = getDataGtmDlFromElement(hostEl);

  // Normalize (assuming you have a normalizeDlFields that can accept either a string or object)
  const dlFields = {
    ...normalizeDlFields(fromDomAttr),
    ...normalizeDlFields(propsDataGtmDl), // props take precedence
  };

  // Get label text (if present)
  const labelEl = componentId
    ? document.querySelector<HTMLLabelElement>(`label[for="${componentId}"]`)
    : null;

  const { category: finalCategory, action: finalAction } =
    getInputDetailsFromElement(hostEl);

  const payload: AnalyticsPayload = {
    event: 'custom_event',
    category: finalCategory,
    action: finalAction ?? 'unknown',
    label: labelEl ? labelEl.textContent?.trim() ?? 'undefined' : 'undefined',
    ...dlFields,
  };

  // SSR-safe push
  const w = (globalThis as any)?.window;
  if (!w) return;
  w.dataLayer = w.dataLayer || [];
  w.dataLayer.push(payload);
}
